<template>
	<div id="app" class="app-container">
		<vue-announcer />
		<ja-loading v-if="$store.state.loading"></ja-loading>
		<ja-alert-pop-up v-if="!alertIsRestricted"></ja-alert-pop-up>
		<ja-onboarding-tutorial
			:show="showGuidedTour"
			:name="$t('guidedTourModalName')"
			@modalChanged="onModalChanged"
		></ja-onboarding-tutorial>
		<ja-pause-modal
			v-if="isPausedSim && !isEducatorOrVolunteer"
			:name="$t('pauseModalName')"
			:show="isPausedSim && !isEducatorOrVolunteer"
			@modalChanged="onModalChanged"
		></ja-pause-modal>
		<ja-session-expire-modal
			v-if="willSessionExpire"
			:name="$t('sessionWillExpireModalName')"
			:show="willSessionExpire"
			@modalChanged="onModalChanged"
		></ja-session-expire-modal>
		<ja-welcome-back-modal
			v-if="showWelcomeBack"
			:show="showWelcomeBack"
			@modalChanged="onModalChanged"
		></ja-welcome-back-modal>
		<ja-session-lost-modal
			:name="$t('userLoginModalName')"
			@modalChanged="onModalChanged"
		></ja-session-lost-modal>
		<ja-log-out-modal
			:name="$t('logoutModalName')"
			@modalChanged="onModalChanged"
		></ja-log-out-modal>
		<router-view ref="view"></router-view>
	</div>
</template>

<script>
import Loading from './components/ui/LoadingBar';
import LogOutModal from './components/Modal/LogOutModal';
import AlertPopUp from './components/ui/AlertPopUp';
import OnboardingTutorial from './components/Modal/OnboardingTutorial';
import PauseModal from './components/Modal/PauseModal';
import ProfileData from './mixins/ProfileData';
import ModalFocusManager from './mixins/ModalFocusManager';
import UserData from '@/mixins/UserData';
import SessionExpireModal from './components/Modal/SessionExpireModal';
import SessionLostModal from './components/Modal/SessionLostModal';
import WelcomeBackModal from './components/Modal/WelcomeBackModal';
import {
	getChooseSimulation,
	getLocalStorageData
} from '@/helpers/storageFunctions';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export default {
	name: 'App',
	components: {
		jaLogOutModal: LogOutModal,
		jaLoading: Loading,
		jaAlertPopUp: AlertPopUp,
		jaOnboardingTutorial: OnboardingTutorial,
		jaPauseModal: PauseModal,
		jaSessionExpireModal: SessionExpireModal,
		jaSessionLostModal: SessionLostModal,
		jaWelcomeBackModal: WelcomeBackModal
	},
	mixins: [ProfileData, UserData, ModalFocusManager],
	data() {
		return {
			activityThrottle: false,
			throttleTimeout: 5000,
			pagesWithoutAlertPopUp: ['home', 'students'],
			alertIsRestricted: false,
			educatorOrVolunteerRole: ['5', '6'],
			sessionCheckInterval: null,
			intervalTime: 5000, // in ms,
			notificationTime: 60, // sec,
			timeGapBeforeLogout: 5 // sec
		};
	},
	computed: {
		sessionLost() {
			return this.$store.getters.sessionLost;
		},
		showWelcomeBack() {
			return (
				this.userData != null &&
				this.userData.lastSimulationPlayedDate != null &&
				this.$store.getters.showWelcomeBack
			);
		},
		showGuidedTour() {
			return this.$store.getters.showOnboarding;
		},
		isPausedSim() {
			if (
				this.$route.path.includes('admin') ||
				this.$route.path.includes('chooseSimulation')
			) {
				return false;
			}
			return this.$store.getters.simPauseState;
		},
		isEducatorOrVolunteer() {
			if (this.userData) {
				return this.educatorOrVolunteerRole.includes(this.userData.roleId);
			} else {
				return null;
			}
		},
		willSessionExpire() {
			return this.$store.getters.sessionWillExpireState;
		},
		constants() {
			return this.$store.getters.constants;
		}
	},
	watch: {
		$route(to) {
			this.alertIsRestricted = this.pagesWithoutAlertPopUp.includes(to.name);
		}
	},
	created() {
		this.$store.dispatch('getConstants');

		this.signalRConnection.on('forcedToNextLifeStage', () => {
			this.$store.commit('setForcedToNextLifeStageState', true);
		});

		// TODO: DELETE THIS - it is not needed anymore
		// this.signalRConnection.on('sessionAboutToExpire', () => {
		// 	this.$store.commit('setSessionWillExpireState', true);
		// });

		// TODO: move this piece of code in worker instead
		this.sessionCheckInterval = setInterval(() => {
			this.checkForSessionExpiration();
		}, this.intervalTime);
	},
	mounted() {
		this.signalRConnection.on('simulationPaused', simId => {
			let userActiveSimId = parseInt(this.userData.userActiveSimulationId);

			if (!userActiveSimId) {
				userActiveSimId = getChooseSimulation('preselectedSim').id;
			}

			if (simId === userActiveSimId) {
				this.$store.commit('setPauseState', true);
			}
		});

		this.signalRConnection.on('simulationResumed', () => {
			this.$store.commit('setPauseState', false);
		});
	},
	beforeDestroy() {
		clearInterval(this.sessionCheckInterval);
	},
	methods: {
		checkForSessionExpiration() {
			// If user is not logged do nothing,
			// because we do not want to notify a user without session
			const storageCookie = getLocalStorageData('ceUTC');
			if (!this.userData || !storageCookie) {
				return;
			}

			// Get cookie date
			// .utc() is needed to use the date as UTC per se
			const ceUTC = dayjs(getLocalStorageData('ceUTC')).utc();
			// Get now time again with UTC
			const now = dayjs().utc();
			// calculate the difference betwen session and now in seconds
			const sessionDiff = ceUTC.diff(now, 'second');

			// Since we are using 5 seconds interval
			// the gap is also set to 5 seconds
			// this is needed to avoid forced logout
			if (sessionDiff < this.timeGapBeforeLogout) {
				// kill the interval as we are going to logout the user
				clearInterval(this.sessionCheckInterval);
				// close the modal if it is visible
				this.$store.commit('setSessionWillExpireState', false);
				// dispatch logout to properly logout user
				this.$store.dispatch('logout');
			}

			// notification time is set to 60 seconds
			// this is needed in order for the app to show the modal
			// atleast one minute before session's expiration
			if (sessionDiff < this.notificationTime) {
				this.$store.commit('setSessionWillExpireState', true);
			} else if (this.willSessionExpire) {
				this.$store.commit('setSessionWillExpireState', false);
			}
		}
	}
};
</script>

<style lang="scss">
@import '@/assets/scss/_global.scss';
@import '@/assets/scss/_components.scss';

.app-container {
	height: 100%;
}
</style>
